import {
  useState,
  useRef,
  useCallback,
  useEffect,
} from 'react';
import type {
  ChatMessage,
  ToolCall,
  StreamEvent,
  ToolCategory,
  Conversation,
} from './types';
import { ChatError } from './types';
import {
  authFetch, getAuthHeaders, API_BASE_URL 
} from '@/lib/api-client';

function generateId(): string {
  const array = new Uint32Array(2);
  crypto.getRandomValues(array);
  return Date.now().toString(36) + array[0].toString(36) + array[1].toString(36).slice(0, 5);
}

interface UseChatReturn {
  messages: ChatMessage[];
  input: string;
  isLoading: boolean;
  activeCategories: ToolCategory[];
  currentToolCall: string | null;
  conversations: Conversation[];
  currentConversationId: string | null;
  isLoadingConversations: boolean;
  inputRef: React.RefObject<HTMLTextAreaElement | null>;
  abortControllerRef: React.RefObject<AbortController | null>;
  setInput: (value: string) => void;
  handleSend: () => Promise<void>;
  loadConversation: (conversationId: string) => Promise<void>;
  startNewConversation: () => void;
  refreshConversations: () => Promise<void>;
  deleteConversation: (conversationId: string) => Promise<void>;
}

export function useChat(): UseChatReturn {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [activeCategories, setActiveCategories] = useState<ToolCategory[]>([]);
  const [currentToolCall, setCurrentToolCall] = useState<string | null>(null);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [currentConversationId, setCurrentConversationId] = useState<string | null>(null);
  const [isLoadingConversations, setIsLoadingConversations] = useState(false);

  const inputRef = useRef<HTMLTextAreaElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Load conversations on mount
  useEffect(() => {
    refreshConversations();
  }, []);

  const refreshConversations = useCallback(async () => {
    setIsLoadingConversations(true);
    try {
      const response = await authFetch(`${API_BASE_URL}/v1/chat/conversations`);
      if (response.ok) {
        const data = await response.json() as { conversations: Conversation[] };
        setConversations(data.conversations);
      }
    } catch (error) {
      console.error('Failed to load conversations:', error);
    } finally {
      setIsLoadingConversations(false);
    }
  }, []);

  const loadConversation = useCallback(async (conversationId: string) => {
    setIsLoading(true);
    try {
      const response = await authFetch(`${API_BASE_URL}/v1/chat/conversations/${conversationId}`);
      if (!response.ok) {
        throw new ChatError(response.status, 'Failed to load conversation');
      }

      const data = await response.json() as {
        conversation: Conversation;
        messages: Array<{
          id: string;
          role: 'user' | 'assistant';
          content: string;
          toolCalls?: ToolCall[];
        }>;
      };

      setCurrentConversationId(conversationId);
      setMessages(data.messages.map((msg) => ({
        id: msg.id,
        role: msg.role,
        content: msg.content,
        toolCalls: msg.toolCalls,
      })));
    } catch (error) {
      console.error('Failed to load conversation:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const startNewConversation = useCallback(() => {
    setCurrentConversationId(null);
    setMessages([]);
    setActiveCategories([]);
  }, []);

  const processEvent = useCallback((
    event: StreamEvent,
    currentContent: string,
    toolCalls: ToolCall[]
  ): string => {
    switch (event.type) {
      case 'text':
        return currentContent + (event.content ?? '');

      case 'tool_use':
        if (event.toolName) {
          setCurrentToolCall(event.toolName);
          toolCalls.push({ name: event.toolName });
        }
        return currentContent;

      case 'tool_result':
        setCurrentToolCall(null);
        if (event.toolName && event.content) {
          const toolCall = toolCalls.find((t) => t.name === event.toolName);
          if (toolCall) {
            toolCall.result = event.content;
          }
        }
        return currentContent;

      case 'categories':
        if (event.categories) {
          setActiveCategories(event.categories);
        }
        return currentContent;

      case 'conversation':
        if (event.conversationId) {
          setCurrentConversationId(event.conversationId);
        }
        return currentContent;

      case 'title':
        // Update the conversation title in the list
        if (event.content) {
          setConversations((prev) => 
            prev.map((conv) => 
              conv.id === currentConversationId
                ? {
                  ...conv,
                  title: event.content ?? conv.title 
                }
                : conv
            )
          );
        }
        return currentContent;

      case 'error':
        throw new ChatError(500, event.content ?? 'Unknown error');

      default:
        return currentContent;
    }
  }, []);

  const readStream = useCallback(async (
    reader: ReadableStreamDefaultReader<Uint8Array>,
    assistantId: string
  ) => {
    const decoder = new TextDecoder();
    const state = {
      buffer: '',
      content: '',
    };
    const toolCalls: ToolCall[] = [];

    while (true) {
      const readResult = await reader.read();
      if (readResult.done) {
        console.log('[Chat] Stream done, final buffer:', state.buffer);
        break;
      }

      const chunk = decoder.decode(readResult.value, { stream: true });
      console.log('[Chat] Received chunk:', chunk);
      state.buffer += chunk;
      const lines = state.buffer.split('\n');
      state.buffer = lines.pop() ?? '';

      for (const line of lines) {
        if (!line.startsWith('data: ')) continue;

        try {
          const event = JSON.parse(line.slice(6)) as StreamEvent;
          console.log('[Chat] Parsed event:', event);
          state.content = processEvent(event, state.content, toolCalls);

          setMessages((prev) => {
            const updated = [...prev];
            const lastMsg = updated.find((m) => m.id === assistantId);
            if (lastMsg) {
              lastMsg.content = state.content;
              lastMsg.toolCalls = [...toolCalls];
            }
            return updated;
          });
        } catch (parseError) {
          console.warn('[Chat] Failed to parse event:', line, parseError);
        }
      }
    }
  }, [processEvent]);

  const processStream = useCallback(async (message: string, signal: AbortSignal) => {
    const headers = await getAuthHeaders();
    console.log('[Chat] Sending request with headers:', Object.keys(headers));
    
    const response = await fetch(`${API_BASE_URL}/v1/chat/stream`, {
      method: 'POST',
      headers: {
        ...headers,
        Accept: 'text/event-stream',
      },
      body: JSON.stringify({
        message,
        conversationId: currentConversationId,
        history: messages.slice(-10).map((m) => ({
          role: m.role,
          content: m.content,
        })),
      }),
      signal,
    });

    console.log('[Chat] Response status:', response.status, response.statusText);
    console.log('[Chat] Response headers:', Object.fromEntries(response.headers.entries()));

    if (!response.ok) {
      throw new ChatError(response.status, `Request failed: ${response.statusText}`);
    }

    const reader = response.body?.getReader();
    if (!reader) {
      throw new ChatError(500, 'No response body');
    }

    const assistantId = generateId();
    setMessages((prev) => [...prev, {
      id: assistantId,
      role: 'assistant',
      content: '',
      toolCalls: [],
    }]);

    await readStream(reader, assistantId);

    // Refresh conversations list to show new/updated conversation
    await refreshConversations();
  }, [currentConversationId, messages, readStream, refreshConversations]);

  const handleStreamError = useCallback((error: unknown) => {
    console.error('[Chat] Stream error:', error);
    
    if (error instanceof Error && error.name === 'AbortError') return;

    const errorMessage = error instanceof ChatError
      ? error.message
      : 'Sorry, something went wrong. Please try again.';

    console.log('[Chat] Displaying error message:', errorMessage);

    setMessages((prev) => {
      const updated = [...prev];
      const lastMsg = updated[updated.length - 1];
      if (lastMsg?.role === 'assistant' && lastMsg.content === '') {
        lastMsg.content = errorMessage;
      } else {
        updated.push({
          id: generateId(),
          role: 'assistant',
          content: errorMessage,
        });
      }
      return updated;
    });
  }, []);

  const handleSend = useCallback(async () => {
    const trimmedInput = input.trim();
    if (trimmedInput === '' || isLoading) return;

    const userMessage: ChatMessage = {
      id: generateId(),
      role: 'user',
      content: trimmedInput,
    };
    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);
    setActiveCategories([]);
    setCurrentToolCall(null);

    abortControllerRef.current = new AbortController();

    try {
      await processStream(trimmedInput, abortControllerRef.current.signal);
    } catch (error) {
      handleStreamError(error);
    } finally {
      setIsLoading(false);
      setCurrentToolCall(null);
    }
  }, [input, isLoading, processStream, handleStreamError]);

  const deleteConversation = useCallback(async (conversationId: string) => {
    try {
      const response = await authFetch(
        `${API_BASE_URL}/v1/chat/conversations/${conversationId}`,
        { method: 'DELETE' }
      );
      
      if (response.ok || response.status === 204) {
        // Remove from local state
        setConversations((prev) => prev.filter((c) => c.id !== conversationId));
        
        // If we deleted the current conversation, clear it
        if (currentConversationId === conversationId) {
          startNewConversation();
        }
      }
    } catch (error) {
      console.error('Failed to delete conversation:', error);
    }
  }, [currentConversationId, startNewConversation]);

  return {
    messages,
    input,
    isLoading,
    activeCategories,
    currentToolCall,
    conversations,
    currentConversationId,
    isLoadingConversations,
    inputRef,
    abortControllerRef,
    setInput,
    handleSend,
    loadConversation,
    startNewConversation,
    refreshConversations,
    deleteConversation,
  };
}
